<template>
	<div
		class="customer-checklist-page"
		:style="themeColors"
	>
		<LoaderComponent
			v-show="isLoading"
			:style="{ color: theme ? theme.altTextColor : 'unset' }"
		/>

		<!--Invalid Access Message-->
		<main
			v-if="!isLoading && !id"
			style="background-color: rgb(51, 51, 51); color: white; justify-content: space-evenly"
		>
			<h1>Valid ID not provided.</h1>
		</main>

		<header v-show="!isLoading && id">
			<div
				class="header-main"
				ref="mainHeader"
			>
				<div class="tool-bar">
					<button
						class="button-refresh"
						@click="onRefreshChecklist"
					>
						<i class="fas fa-sync" />
					</button>
					<button
						class="button-home"
						:disabled="showListView"
						@click="onExitItemUpdate(uploadComplete)"
					>
						<i class="fas fa-home" />
					</button>
				</div>

				<div class="customer-and-store">
					<h1>Checklist</h1>
					<h3>{{ customerName }}</h3>
				</div>
			</div>

			<ErrorBanner
				:hasError="showError"
				:message="errorMessage"
				@close="() => (showError = false)"
			/>
		</header>

		<main
			v-show="!isLoading && id"
			ref="checklistMain"
		>
			<div :class="{ 'back-button-container': true, 'is-visible': !showListView }">
				<button
					class="button-back"
					@click="onExitItemUpdate(uploadComplete)"
				>
					<i class="fas fa-chevron-circle-left" /> BACK
				</button>
			</div>

			<transition
				v-if="itemToUpdate"
				:name="transitionAnimation"
			>
				<CustomerChecklistDocUpload
					v-if="showDocUpload"
					class="action-screen"
					:dealId="dealId"
					:checklistItem="itemToUpdate"
					@uploadComplete="() => (uploadComplete = true)"
					@exitItem="onExitItemUpdate(uploadComplete)"
					@error="onError"
				/>

				<CustomerChecklistPlaidLink
					v-else-if="showPlaidLink"
					class="action-screen"
					:dealId="dealId"
					:storeName="storeName"
					:checklistItem="itemToUpdate"
					:receivedRedirectUrl="plaidReceivedRedirectUrl"
					@uploadComplete="() => (uploadComplete = true)"
					@exitItem="onExitItemUpdate(uploadComplete)"
					@error="onError"
				/>

				<CustomerChecklistVisitLink
					v-else-if="showVisitLink"
					class="action-screen"
					:checklistItem="itemToUpdate"
				/>

				<CustomerChecklistViewItem
					v-else-if="showItemView"
					class="action-screen"
					:dealId="dealId"
					:dealRole="dealRole"
					:checklistItem="itemToUpdate"
					:onUpdateItem="onUpdateItem"
					@deleteComplete="() => onExitItemUpdate(true)"
					@error="onError"
				/>
			</transition>

			<div
				v-show="showListView && checklist.length > 0"
				class="checklist-and-tabs"
			>
				<nav class="tab-group">
					<button
						:class="{ 'tab-header': true, active: showToDoItems }"
						@click="showToDoItems = true"
					>
						<h2>TO-DO</h2>
					</button>
					<button
						:class="{ 'tab-header': true, active: !showToDoItems }"
						@click="showToDoItems = false"
					>
						<h2>COMPLETED</h2>
					</button>
				</nav>

				<div class="tab-content">
					<section
						v-if="checklistOrganized.length < 1"
						class="empty-list-message"
					>
						<h2 v-if="showToDoItems">All items have been completed.</h2>
						<h2 v-else>Nothing completed yet.</h2>
					</section>

					<CustomerChecklistItem
						v-for="item in checklistOrganized"
						:key="item.id"
						:dealId="dealId"
						:checklistItem="item"
						:onUpdateItem="onUpdateItem"
						:onViewItem="onViewItem"
					/>
				</div>
			</div>

			<h2 v-if="checklist.length < 1">You have no pending checklist items.</h2>
		</main>
	</div>
</template>
<script setup lang="ts">
	import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
	import api from '@core/services/api/_api';
	import { useRoute } from 'vue-router';
	import util from '@core/services/util';

	import ApplicationsSettingsLoader from '@core/classes/Applications/ApplicationsSettingsLoader';
	import DealChecklistItem from '@core/classes/Checklist/DealChecklistItem';
	import ENUMS from '@core/classes/Enums';

	import CustomerChecklistDocUpload from '@/components/customerChecklist/CustomerChecklistDocUpload.vue';
	import CustomerChecklistItem from '@/components/customerChecklist/CustomerChecklistItem.vue';
	import CustomerChecklistPlaidLink from '@/components/customerChecklist/CustomerChecklistPlaidLink.vue';
	import CustomerChecklistViewItem from '@/components/customerChecklist/CustomerChecklistViewItem.vue';
	import CustomerChecklistVisitLink from '@/components/customerChecklist/CustomerChecklistVisitLink.vue';
	import ErrorBanner from '@/components/ErrorBanner.vue';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';

	const route = useRoute();
	const checklistMainRef = useTemplateRef('checklistMain');
	const mainHeaderRef = useTemplateRef('mainHeader');

	const id = ref<string>(null);

	const dealId = ref<string>(null);
	const storeName = ref<string>(null);
	const customerName = ref<string>(null);
	const dealRole = ref<number>(null);

	const isLoading = ref(false);

	// ===============
	// LIFECYCLE HOOKS
	// ===============
	// #region Lifecycle Hooks
	onBeforeMount(async () => {
		id.value = route.query.id.toString() ?? localStorage.getItem('customer_checklist_id');
		if (!id.value) return;

		localStorage.setItem('customer_checklist_id', id.value);

		isLoading.value = true;

		await onRefreshChecklist();

		// Is this a Plaid OAuth redirect?
		const haveEnoughDataToRelaunch = localStorage.getItem('link_token') && localStorage.getItem('plaid_checklist_session');
		if (route.query.oauth_state_id && haveEnoughDataToRelaunch) {
			plaidReceivedRedirectUrl.value = window.location.href;
			onUpdateItem(localStorage.getItem('plaid_checklist_session'));
		}

		isLoading.value = false;
	});

	onMounted(() => {
		headerResizeObserver.value = new ResizeObserver(onHeaderResize);
		if (mainHeaderRef.value) headerResizeObserver.value.observe(mainHeaderRef.value);
	});

	onBeforeUnmount(() => {
		headerResizeObserver.value.disconnect();
	});
	// #endregion

	// ==============
	// ERROR HANDLING
	// ==============
	// #region Error Handling
	const showError = ref(false);
	const errorMessage = ref('An error has occurred.');

	const onError = (message: string) => {
		errorMessage.value = message;
		showError.value = true;
	};
	// #endregion

	// ==============
	// CHECKLIST DATA
	// ==============
	// #region Checklist Data
	const checklist = ref<DealChecklistItem[]>([]);
	const currentItemId = ref<string>(null);

	const checklistOrganized = computed(() => {
		return checklist.value.filter(c => {
			const status = c.getOverallStatus();
			if (showToDoItems.value) return status === 'REJECTED' || status === 'UNVERIFIED';
			else return status === 'PENDING' || status === 'APPROVED';
		});
	});

	const itemToUpdate = computed(() => {
		if (!currentItemId.value) return null;
		return checklist.value.find(i => i.id === currentItemId.value);
	});

	const sortChecklist = () => {
		// Order: Unverified Items => Partially Verified Items => Verified Items
		const getSortValue = (a: DealChecklistItem) => {
			if (a.status.isVerified) return 2;
			else if (a.status.autoVerified) return 1;
			return 0;
		};

		checklist.value.sort((a, b) => {
			const aVeri = getSortValue(a);
			const bVeri = getSortValue(b);
			return aVeri - bVeri;
		});
	};

	const onRefreshChecklist = async () => {
		isLoading.value = true;

		try {
			const dealResponse = await api.checklist.getChecklistForCustomer(id.value);

			if (dealResponse && dealResponse.data) {
				dealId.value = dealResponse.data.dealId;
				dealRole.value = dealResponse.data.dealRole;
				customerName.value = dealResponse.data.customerName;
				storeName.value = dealResponse.data.storeName;

				if (!dealResponse.data.theme.mainColor) {
					await ApplicationsSettingsLoader.init();
					theme.value = ApplicationsSettingsLoader.ApplicationSettings.theme;
				} else {
					theme.value = dealResponse.data.theme;
				}

				checklist.value = [];
				checklist.value = dealResponse.data.checklistItems.map((c: Partial<DealChecklistItem>) => new DealChecklistItem(c));

				sortChecklist();
			} else {
				await ApplicationsSettingsLoader.init();

				id.value = null;
				theme.value = ApplicationsSettingsLoader.ApplicationSettings.theme;
			}
		} catch (err) {
			console.error('ERROR REFRESHING CHECKLIST: ', err);
			onError('An error occurred when trying to load in checklist data.');
		} finally {
			isLoading.value = false;
		}
	};
	// #endregion

	// ============
	// THEME COLORS
	// ============
	// #region Theme Colors
	const theme = ref<{ [key: string]: string }>(null);

	const themeColors = computed(() => {
		if (!theme.value) return {};

		const themeKeys = Object.keys(theme.value);
		const colorObj: { [key: string]: string } = {};
		themeKeys.forEach(k => {
			const kebab = k.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? '-' : '') + $.toLowerCase());
			colorObj[`--${kebab}`] = theme.value[k];
		});

		return colorObj;
	});
	// #endregion

	// ===============
	// RESIZE OBSERVER
	// ===============
	// #region Resize Observer
	const headerResizeObserver = ref<ResizeObserver>(null);
	const resizeCount = ref(0);

	const onHeaderResize = () => {
		util.debounce(onHeaderResizeDebounced, 150);
	};

	const onHeaderResizeDebounced = () => {
		//Prevents header from overlapping main content
		const headerElem = mainHeaderRef.value;
		const mainElem = checklistMainRef.value;

		if (!headerElem || mainElem.style.display === 'none') return;

		if (headerElem.clientWidth < 800) mainElem.style.paddingTop = `${headerElem.clientHeight}px`;
		else mainElem.style.removeProperty('padding-top');

		resizeCount.value++;
	};
	// #endregion

	// ================
	// TOGGLE LIST VIEW
	// ================
	// #region Toggle List View
	const showListView = ref(true);
	const listViewTimeout = ref<NodeJS.Timeout>(null);

	const transitionAnimation = computed(() => {
		if (resizeCount.value == null) return null;
		return window.innerWidth < 800 ? 'slide-up-transition' : null;
	});

	const toggleListScreen = (showScreen: boolean) => {
		if (listViewTimeout.value) {
			clearTimeout(listViewTimeout.value);
			listViewTimeout.value = null;
		}

		// For now, there's no transition animations on desktop
		if (window.innerWidth > 800) {
			showListView.value = showScreen;
			return;
		}

		//TODO: make animation less nauseating, this gets grosser the longer the page is
		document.scrollingElement.scrollTo({ top: 0, behavior: 'smooth' });

		if (showScreen) showListView.value = showScreen;

		listViewTimeout.value = setTimeout(() => {
			if (!showScreen) showListView.value = showScreen;
			listViewTimeout.value = null;
		}, 700);
	};
	// #endregion

	// ===========================
	// CHECKLIST SCREEN MANAGEMENT
	// ===========================
	// #region Checklist Screen Management
	const showDocUpload = ref(false);
	const showPlaidLink = ref(false);
	const showVisitLink = ref(false);
	const showItemView = ref(false);
	const showToDoItems = ref(true);

	const uploadComplete = ref(false);
	const plaidReceivedRedirectUrl = ref<string>(null);

	const onUpdateItem = (itemId: string) => {
		currentItemId.value = itemId;

		if (itemToUpdate.value.isTypePlaid()) {
			showPlaidLink.value = true;
			showDocUpload.value = false;
			showVisitLink.value = false;
		} else if (itemToUpdate.value.type === ENUMS.DealChecklistType.VisitLink) {
			showPlaidLink.value = false;
			showDocUpload.value = false;
			showVisitLink.value = true;
		} else {
			showPlaidLink.value = false;
			showDocUpload.value = true;
			showVisitLink.value = false;
		}

		toggleListScreen(false);
	};

	const onExitItemUpdate = async (submitSuccess: boolean) => {
		if (submitSuccess) {
			isLoading.value = true;

			const index = checklist.value.findIndex(i => i.id === currentItemId.value);

			const response = await api.checklist.customer_getUpdatedItem(dealId.value, currentItemId.value, dealRole.value, checklist.value[index].setIndex);
			if (response && response.data) {
				checklist.value[index] = new DealChecklistItem(response.data);
				sortChecklist();
			} else {
				onError('Error: unable to refresh checklist.');
			}

			isLoading.value = false;
		}

		currentItemId.value = null;
		toggleListScreen(true);
		showPlaidLink.value = false;
		showDocUpload.value = false;
		showVisitLink.value = false;
		showItemView.value = false;
		uploadComplete.value = false;
	};

	const onViewItem = (itemId: string) => {
		currentItemId.value = itemId;
		toggleListScreen(false);

		showPlaidLink.value = false;
		showDocUpload.value = false;
		showVisitLink.value = false;
		showItemView.value = true;
	};
	// #endregion
</script>
<style>
	.customer-checklist-page > header {
		width: 100%;
		display: unset;
		position: fixed;
		top: 0;
		box-shadow: rgba(0, 0, 0, 0.5) 1px 1px 10px;
		color: var(--alt-text-color);
		z-index: 10;
	}

	.customer-checklist-page > header .header-main {
		position: relative;
		padding: 20px 0;
		background-color: var(--main-color);
	}

	.customer-checklist-page > header .header-main .tool-bar {
		position: absolute;
		right: 1em;
		top: 50%;
		transform: translateY(-50%);
	}

	.customer-checklist-page > header .header-main .tool-bar > button {
		background-color: transparent;
		border: none;
		margin: 0 10px;
		transition: all 0.3s ease-in-out;
	}

	.customer-checklist-page > header .header-main .tool-bar > button[disabled] {
		opacity: 0.5;
		background-color: unset !important;
	}

	.customer-checklist-page > header .header-main .tool-bar > button i {
		color: var(--alt-text-color);
		font-size: 2em;
		transition: all 0.3s ease-in-out;
	}

	.customer-checklist-page > header .header-main .tool-bar .button-refresh:hover i {
		transform: rotate(180deg);
	}

	.customer-checklist-page > header .header-main .customer-and-store {
		display: flex;
		justify-content: space-evenly;
		text-transform: uppercase;
	}

	.customer-checklist-page > header .header-errors {
		position: relative;
		padding: 20px;
		background-color: var(--error-button-color);
	}

	.customer-checklist-page > header .header-errors .button-close {
		position: absolute;
		right: 20px;
		top: 50%;
		transform: translateY(-50%);
		background-color: transparent;
		border: none;
		color: var(--alt-text-color);
	}

	.customer-checklist-page > header .header-errors .button-close i {
		font-size: 1.5em;
	}

	.customer-checklist-page > main {
		width: 600px;
		min-height: 400px;
		padding: 30px;
		justify-content: space-evenly;
		position: absolute;
		top: 100px;
		left: 50%;
		bottom: auto;
		transform: translateX(-50%);
		border-radius: 10px;
		color: var(--alt-text-color);
		transition: all 0.8s ease-in-out;
	}

	.customer-checklist-page > main > .back-button-container {
		width: 100%;
		height: 0px;
		transition: all 0.3s ease-in-out;
	}
	.customer-checklist-page > main > .back-button-container.is-visible {
		height: unset;
		padding: 5px;
	}

	.customer-checklist-page > main > .back-button-container > .button-back {
		visibility: hidden;
		border: none;
		background-color: transparent;
		font-weight: bold;
		font-size: 0;
		color: var(--main-color);
		transition: all 0.3s ease-in-out;
	}
	.customer-checklist-page > main > .back-button-container:hover > .button-back {
		color: var(--accept-color);
	}
	.customer-checklist-page > main > .back-button-container.is-visible > .button-back {
		visibility: visible;
		font-size: 1.2em;
	}

	.customer-checklist-page > main > section.action-screen {
		width: 100%;
		background-color: var(--main-color);
		border-radius: 10px;
	}

	.customer-checklist-page > main > section.action-screen .spinner-container {
		border-radius: 10px;
		background-color: transparent;
	}

	.customer-checklist-page > main > section:not(.deal-checklist-item-container) {
		padding: 15px;
	}

	.customer-checklist-page > main .warning-message {
		padding: 5px;
		border-radius: 10px;
		font-weight: bold;
		background-color: var(--error-button-color);
	}

	.customer-checklist-page > main .checklist-and-tabs {
		width: 100%;
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		z-index: -1;
	}

	.customer-checklist-page > main .checklist-and-tabs .tab-group {
		padding: 15px 15px 0 15px;
	}

	.customer-checklist-page > main .checklist-and-tabs .tab-group .tab-header {
		margin: 0 5px;
		padding: 5px 5px 0 5px;
		background-color: var(--secondary-text-color);
		color: var(--alt-text-color);
		border-radius: 10px 10px 0 0;
		border: none;
		border-bottom: 1px solid var(--border-color);
		filter: brightness(0.8);
		transition: all 0.3s;
	}

	.customer-checklist-page > main .checklist-and-tabs .tab-group .tab-header:hover {
		filter: brightness(1);
	}

	.customer-checklist-page > main .checklist-and-tabs .tab-group .tab-header:active {
		filter: brightness(1.2);
	}

	.customer-checklist-page > main .checklist-and-tabs .tab-group .tab-header.active {
		color: var(--secondary-text-color);
		filter: brightness(1);
		background-color: var(--secondary-color);
	}

	.customer-checklist-page > main .checklist-and-tabs .tab-content {
		padding: 15px;
		flex-grow: 1;
		background-color: var(--secondary-color);
		border-radius: 10px;
	}

	.customer-checklist-page > main .checklist-and-tabs .tab-content .empty-list-message {
		text-align: center;
		color: var(--main-text-color);
	}

	.customer-checklist-page .button-slant-split {
		display: flex;
		justify-content: space-evenly;
	}

	.customer-checklist-page .button-slant-split > * {
		width: 100%;
		margin-left: -10px;
		margin-right: -10px;
		clip-path: polygon(20% 0, 100% 0, 80% 100%, 0 100%);
		font-weight: bold;
	}

	/* If only child */
	.customer-checklist-page .button-slant-split > *:first-child:last-child {
		margin: unset !important;
		border-radius: 10px !important;
		clip-path: unset !important;
	}

	.customer-checklist-page .button-slant-split > *:first-child {
		margin-left: unset;
		border-radius: 10px 0 0 10px;
		clip-path: polygon(0 0, 100% 0, 80% 100%, 0 100%);
	}

	.customer-checklist-page .button-slant-split > *:last-child {
		margin-right: unset;
		border-radius: 0 10px 10px 0;
		clip-path: polygon(20% 0, 100% 0, 100% 100%, 0 100%);
	}

	.customer-checklist-page .stretch-to-fit {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	}

	button.button-link {
		width: fit-content;
		height: fit-content;
		padding: inherit;
		border: none;
		background-color: transparent;
		text-align: inherit;
		font-size: inherit;
		color: var(--link-alt-text-color);
		text-decoration: underline;
		box-shadow: none;
	}

	button.button-link:hover {
		filter: brightness(1.5);
	}

	button.button-link:disabled {
		filter: grayscale(1);
		text-decoration: none;
		border: none !important;
	}

	fieldset {
		border: none;
	}

	.input-radio.sfx:hover,
	.input-checkbox.sfx:hover {
		color: var(--accept-color);
	}

	.error-transition-enter-active,
	.error-transition-leave-active {
		opacity: 1;
		/*max-height: 200px;*/
		transition: all 0.3s linear;
	}

	.error-transition-enter-from,
	.error-transition-leave-to {
		opacity: 0;
		max-height: 0;
		overflow: hidden;
	}

	@media only screen and (max-width: 800px) {
		.customer-checklist-page > header {
			display: block;
		}

		.customer-checklist-page > header .header-main {
			display: block;
			padding: 20px;
		}

		.customer-checklist-page > header .header-main .customer-and-store {
			width: unset;
			display: block;
			padding-right: 6em;
		}

		.customer-checklist-page > main {
			width: 100%;
			min-height: 100vh;
			padding: unset;
			justify-content: unset;
			position: relative;
			transform: unset;
			top: 0;
			bottom: 0;
			left: 0;
			border-radius: 0;
		}

		.customer-checklist-page > main p,
		.customer-checklist-page > main b,
		.customer-checklist-page > main p > span {
			font-size: 20px;
		}

		.customer-checklist-page > main > section.action-screen {
			border-radius: unset;
		}

		.customer-checklist-page > main > section:not(.deal-checklist-item-container) {
			padding: 15px;
			flex-grow: 1;
		}

		.customer-checklist-page > main .checklist-and-tabs .tab-content {
			border-radius: unset;
		}

		.slide-up-transition-enter-active,
		.slide-up-transition-leave-active {
			position: absolute;
			height: 100%;
			transform: translateY(0);
			transition: all 0.7s ease-in-out;
		}

		.slide-up-transition-enter-from,
		.slide-up-transition-leave-to {
			transform: translateY(calc(100vh + var(--scroll-position)));
			overflow: hidden;
		}
	}
</style>
